/**
 * Global PMS dashboard statistics API + margin sync into `localStorage.sharedData`.
 * Use after login (prefetch) and from Dashboard / Portfolio polling.
 */

import { normalizeBrokerFlag, useBrokerStatisticsStore } from "../stores/brokerStatisticsStore";

const DEFAULT_PMS_BASE =
  process.env.REACT_APP_PMS_API_BASE_URL || "https://pmsapi.infomanav.in";

/** Fired when `sharedData.margin` is updated from statistics (same-tab listeners). */
export const PMS_SHARED_DATA_MARGIN_EVENT = "pms-shared-data-margin";

function readBrokerFlagFromStorage() {
  const direct = localStorage.getItem("broker_type");
  if (direct) return String(direct).trim().toLowerCase();
  try {
    const sd = JSON.parse(localStorage.getItem("sharedData") || "null");
    return String(sd?.broker_type || "").trim().toLowerCase();
  } catch {
    return "";
  }
}

/**
 * Merges PMS dashboard `margin` into `localStorage.sharedData` only when the numeric value changes,
 * then dispatches {@link PMS_SHARED_DATA_MARGIN_EVENT}.
 */
export function syncDashboardMarginToSharedStorage(marginFromApi) {
  if (marginFromApi === undefined || marginFromApi === null) return;

  const nextNum = Number(marginFromApi);
  if (!Number.isFinite(nextNum)) return;

  const raw = localStorage.getItem("sharedData");
  if (!raw) return;

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return;
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return;

  const prevVal = parsed.margin;
  const prevNum =
    prevVal === undefined || prevVal === null || prevVal === ""
      ? Number.NaN
      : Number(prevVal);

  if (Number.isFinite(prevNum) && prevNum === nextNum) {
    return;
  }

  parsed.margin = nextNum;
  try {
    localStorage.setItem("sharedData", JSON.stringify(parsed));
  } catch {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(PMS_SHARED_DATA_MARGIN_EVENT, { detail: { margin: nextNum } }),
  );
}

/**
 * POST `/api/v1/dashboard/statistics`.
 *
 * @param {object} [options]
 * @param {string} [options.pmsApiBaseUrl]
 * @param {string} [options.email] — defaults to `localStorage.email`
 * @param {string} [options.brokerFlag] — defaults to `broker_type` / `sharedData.broker_type`
 * @param {boolean} [options.syncMargin=true] — merge `stats.margin` into `sharedData` when present
 * @returns {Promise<{ ok: boolean, stats: object|null, reason?: string, status?: number }>}
 */
export async function fetchDashboardStatistics(options = {}) {
  const pmsApiBaseUrl = options.pmsApiBaseUrl || DEFAULT_PMS_BASE;
  const email =
    options.email !== undefined && options.email !== null
      ? String(options.email)
      : localStorage.getItem("email") || "";
  const brokerFlag = String(
    options.brokerFlag !== undefined && options.brokerFlag !== null
      ? options.brokerFlag
      : readBrokerFlagFromStorage(),
  ).trim().toLowerCase();

  const syncMargin = options.syncMargin !== false;

  if (!email || !brokerFlag) {
    return { ok: false, stats: null, reason: "missing_credentials" };
  }

  try {
    const response = await fetch(`${pmsApiBaseUrl}/api/v1/dashboard/statistics`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        broker_flag: brokerFlag,
        email,
      }),
    });

    if (!response.ok) {
      return {
        ok: false,
        stats: null,
        reason: "http_error",
        status: response.status,
      };
    }

    const payload = await response.json();
    const stats = payload?.data?.data || null;

    if (syncMargin) {
      syncDashboardMarginToSharedStorage(stats?.margin);
    }

    return { ok: true, stats };
  } catch (error) {
    console.error("[dashboardStatisticsService] fetch failed:", error);
    return { ok: false, stats: null, reason: "network", error };
  }
}

/**
 * Fetches dashboard statistics for inactive linked brokers and writes them into the store.
 * Active broker is left to {@link useBrokerStatisticsPolling} (immediate + 2s interval).
 *
 * @param {object} [options]
 * @param {string} [options.pmsApiBaseUrl]
 * @param {string} [options.email]
 * @param {string} [options.activeBrokerFlag]
 * @returns {Promise<Array<{ broker_flag: string, ok: boolean, stats: object|null, reason?: string }>>}
 */
export async function hydrateAllBrokerStatistics(options = {}) {
  const pmsApiBaseUrl = options.pmsApiBaseUrl || DEFAULT_PMS_BASE;
  const email =
    options.email !== undefined && options.email !== null
      ? String(options.email)
      : localStorage.getItem("email") || "";
  const activeBrokerFlag = normalizeBrokerFlag(
    options.activeBrokerFlag !== undefined && options.activeBrokerFlag !== null
      ? options.activeBrokerFlag
      : readBrokerFlagFromStorage(),
  );

  const store = useBrokerStatisticsStore.getState();
  if (!store.isInitialized) {
    store.initializeFromUserBrokers();
  }

  const brokers = readUserBrokersFromStorage();
  const brokerFlags = [
    ...new Set(
      (Array.isArray(brokers) ? brokers : [])
        .map((broker) => normalizeBrokerFlag(broker?.broker_flag))
        .filter(Boolean)
        .filter((flag) => flag !== activeBrokerFlag),
    ),
  ];

  if (!email || brokerFlags.length === 0) {
    return [];
  }

  const results = await Promise.all(
    brokerFlags.map(async (brokerFlag) => {
      const result = await fetchDashboardStatistics({
        pmsApiBaseUrl,
        email,
        brokerFlag,
        syncMargin: false,
      });

      if (result.ok && result.stats) {
        store.setBrokerStats(brokerFlag, result.stats, { applyZeroGuard: false });
      }

      return {
        broker_flag: brokerFlag,
        ok: result.ok,
        stats: result.stats,
        reason: result.reason,
      };
    }),
  );

  return results;
}

/**
 * Run statistics once after login (non-blocking). Safe to fire-and-forget.
 * Prefer {@link hydrateAllBrokerStatistics} on login so stats are ready before navigation.
 *
 * @param {object} [overrides] — forwarded to {@link fetchDashboardStatistics}
 */
export function prefetchDashboardStatisticsAfterLogin(overrides = {}) {
  const brokerFlag = normalizeBrokerFlag(
    overrides.brokerFlag !== undefined && overrides.brokerFlag !== null
      ? overrides.brokerFlag
      : readBrokerFlagFromStorage(),
  );

  return fetchDashboardStatistics(overrides)
    .then((result) => {
      if (!result.ok || !result.stats) return result;

      const store = useBrokerStatisticsStore.getState();
      if (!store.isInitialized) {
        store.initializeFromUserBrokers();
      }
      store.setBrokerStats(brokerFlag, result.stats, { applyZeroGuard: false });

      return result;
    })
    .catch((err) => {
      console.warn("[dashboardStatisticsService] post-login prefetch failed:", err);
    });
}

function readUserBrokersFromStorage() {
  try {
    const parsed = JSON.parse(localStorage.getItem("user_brokers") || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * Fetches dashboard statistics for every broker linked to the user.
 *
 * @param {object} [options]
 * @param {string} [options.pmsApiBaseUrl]
 * @param {string} [options.email]
 * @param {Array} [options.brokers] — defaults to `localStorage.user_brokers`
 * @param {string} [options.activeBrokerFlag] — only this broker syncs margin into `sharedData`
 * @returns {Promise<Array<{ broker_flag: string, ok: boolean, stats: object|null, reason?: string }>>}
 */
export async function fetchAllBrokersDashboardStatistics(options = {}) {
  const pmsApiBaseUrl = options.pmsApiBaseUrl || DEFAULT_PMS_BASE;
  const email =
    options.email !== undefined && options.email !== null
      ? String(options.email)
      : localStorage.getItem("email") || "";
  const brokers = options.brokers ?? readUserBrokersFromStorage();
  const activeBrokerFlag = String(
    options.activeBrokerFlag !== undefined && options.activeBrokerFlag !== null
      ? options.activeBrokerFlag
      : readBrokerFlagFromStorage(),
  )
    .trim()
    .toLowerCase();

  const brokerFlags = [
    ...new Set(
      (Array.isArray(brokers) ? brokers : [])
        .map((broker) => String(broker?.broker_flag || "").trim().toLowerCase())
        .filter(Boolean)
        .filter((flag) => flag !== activeBrokerFlag),
    ),
  ];

  if (!email || brokerFlags.length === 0) {
    return [];
  }

  const results = await Promise.all(
    brokerFlags.map(async (brokerFlag) => {
      const result = await fetchDashboardStatistics({
        pmsApiBaseUrl,
        email,
        brokerFlag,
        syncMargin: brokerFlag === activeBrokerFlag,
      });

      return {
        broker_flag: brokerFlag,
        ok: result.ok,
        stats: result.stats,
        reason: result.reason,
      };
    }),
  );

  return results;
}
